/**
 * Mnemonic Display Component
 *
 * Shows the recovery phrase for backup
 */

import { useState } from "react";
import { Copy, Check, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/useToast";
import { useBrowserWallet } from "@nullaone/wallet-react";

interface MnemonicDisplayProps {
  mnemonic?: string;
}

export function MnemonicDisplay({
  mnemonic: propMnemonic,
}: MnemonicDisplayProps) {
  const { t } = useTranslation();
  const { closeModal, markBackedUp } = useBrowserWallet();
  const { error: showError } = useToast();
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // This component can receive mnemonic as prop (during creation)
  // or could be used standalone for backup display
  const mnemonic = propMnemonic;

  if (!mnemonic) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">
          {t("wallet.mnemonicDisplay.empty")}
        </p>
      </div>
    );
  }

  const words = mnemonic.split(" ");

  const handleCopy = async () => {
    await navigator.clipboard.writeText(mnemonic);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDone = async () => {
    if (!confirmed) return;
    try {
      await markBackedUp();
    } catch (error) {
      // The wallet exists and is usable; needsBackup stays true so the
      // backup banner keeps nagging. Don't trap the user on this screen.
      console.error("markBackedUp failed:", error);
      showError(t("wallet.mnemonicDisplay.markBackedUpFailed"));
    }
    closeModal();
  };

  return (
    <div className="space-y-6 py-4">
      {/* Warning */}
      <div className="rounded-lg border border-warning/40 bg-warning/10 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-warning-dark mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-warning-dark">
              {t("wallet.mnemonicDisplay.warningTitle")}
            </p>
            <p className="text-xs text-warning-dark/80">
              {t("wallet.mnemonicDisplay.warningDesc")}
            </p>
          </div>
        </div>
      </div>

      {/* Mnemonic grid */}
      <div className="grid grid-cols-3 gap-2">
        {words.map((word, index) => (
          <div
            key={index}
            className="flex items-center gap-2 rounded-md border border-border-card bg-bg-page px-3 py-2 text-sm"
          >
            <span className="text-xs text-text-muted w-5 text-right font-mono tabular-nums">
              {index + 1}.
            </span>
            <span className="font-mono font-medium text-text-dark">{word}</span>
          </div>
        ))}
      </div>

      {/* Copy button */}
      <Button variant="outline" className="w-full gap-2" onClick={handleCopy}>
        {copied ? (
          <>
            <Check className="h-4 w-4" />
            {t("wallet.mnemonicDisplay.copied")}
          </>
        ) : (
          <>
            <Copy className="h-4 w-4" />
            {t("wallet.mnemonicDisplay.copy")}
          </>
        )}
      </Button>

      {/* Confirmation checkbox */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="confirm-saved"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300"
        />
        <Label htmlFor="confirm-saved" className="text-sm cursor-pointer">
          {t("wallet.mnemonicDisplay.confirmLabel")}
        </Label>
      </div>

      {/* Done button */}
      <Button
        className="w-full"
        size="lg"
        disabled={!confirmed}
        onClick={handleDone}
      >
        {t("wallet.mnemonicDisplay.done")}
      </Button>
    </div>
  );
}
